import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ConsultationModal from "@/components/ConsultationModal";
import { FileText, Clock, CheckCircle, AlertCircle, ArrowRight, Globe, Plane, Shield, HelpCircle } from "lucide-react";

const visaInfo = {
  overview: {
    title: "Medical Visa for India",
    description: "India offers a dedicated Medical Visa (M-Visa) for international patients seeking treatment at Indian hospitals. This visa type is specifically designed for patients requiring specialized medical care and allows multiple entries within the visa validity period.",
    features: [
      "Valid for up to 1 year with multiple entries",
      "Allows stay up to 60 days per visit",
      "Can be extended based on medical necessity",
      "Attendant visas available for family members"
    ]
  },
  requirements: [
    "Valid passport with at least 6 months validity",
    "Recent passport-sized photographs",
    "Medical documents/diagnosis reports",
    "Hospital invitation letter from Indian hospital",
    "Proof of sufficient funds",
    "Return flight ticket or itinerary",
    "Medical insurance (recommended)"
  ],
  process: [
    { step: 1, title: "Consultation", desc: "Send us your medical reports for evaluation. We connect you with appropriate hospitals." },
    { step: 2, title: "Hospital Letter", desc: "Once treatment is confirmed, we obtain the invitation letter from the hospital." },
    { step: 3, title: "Visa Application", desc: "Apply online at the Indian embassy website. We guide you through the process." },
    { step: 4, title: "Approval", desc: "Medical visas are typically processed within 5-10 working days." },
  ],
  countries: [
    { name: "Nigeria", flag: "🇳🇬", processing: "5-7 days", embassy: "Abuja, Lagos", notes: "High Commission of India processes medical visas with express options available. Over 1,200 Nigerians visit India annually for treatment.", urgent: true, eVisa: true },
    { name: "Kenya", flag: "🇰🇪", processing: "5-7 days", embassy: "Nairobi", notes: "Apply through VFS Global center or directly at the High Commission. E-visa available for medical purposes.", eVisa: true },
    { name: "Ghana", flag: "🇬🇭", processing: "5-7 days", embassy: "Accra", notes: "VFS Global handles applications. Growing medical tourism hub with direct flights to India.", eVisa: true },
    { name: "Tanzania", flag: "🇹🇿", processing: "5-7 days", embassy: "Dar es Salaam", notes: "High Commission provides medical visa services. Direct flight connectivity available.", eVisa: true },
    { name: "Uganda", flag: "🇺🇬", processing: "5-7 days", embassy: "Kampala", notes: "Express processing available for medical emergencies. Visa on arrival for certain conditions.", urgent: true, eVisa: true },
    { name: "Ethiopia", flag: "🇪🇹", processing: "5-7 days", embassy: "Addis Ababa", notes: "Apply at Indian Embassy. Ethiopian Airlines offers direct Delhi connectivity.", eVisa: true },
    { name: "South Africa", flag: "🇿🇦", processing: "7-10 days", embassy: "Pretoria, Johannesburg", notes: "Apply through VFS Global. Medical visa requires detailed treatment plan.", eVisa: false },
    { name: "UAE", flag: "🇦🇪", processing: "3-5 days", embassy: "Dubai, Abu Dhabi", notes: "Fast-track processing available. Multiple daily flights to major Indian cities.", urgent: true, eVisa: true },
    { name: "Saudi Arabia", flag: "🇸🇦", processing: "5-7 days", embassy: "Riyadh, Jeddah", notes: "BLS International handles processing. Hospital letter mandatory.", eVisa: true },
    { name: "Oman", flag: "🇴🇲", processing: "3-5 days", embassy: "Muscat", notes: "Quick processing. Strong medical tourism ties with India.", urgent: true, eVisa: true },
    { name: "Qatar", flag: "🇶🇦", processing: "3-5 days", embassy: "Doha", notes: "Express visa processing. Regular flights to all major Indian cities.", urgent: true, eVisa: true },
    { name: "Kuwait", flag: "🇰🇼", processing: "5-7 days", embassy: "Kuwait City", notes: "BLS International application center. E-medical visa available.", eVisa: true },
    { name: "Bahrain", flag: "🇧🇭", processing: "3-5 days", embassy: "Manama", notes: "Fast processing. Direct connectivity to Chennai, Mumbai, Delhi.", urgent: true, eVisa: true },
    { name: "Bangladesh", flag: "🇧🇩", processing: "3-5 days", embassy: "Dhaka, Chittagong", notes: "Largest medical tourism source. Expedited processing common. Land border entry possible.", urgent: true, eVisa: true },
    { name: "Nepal", flag: "🇳🇵", processing: "Same day", embassy: "Kathmandu", notes: "No visa required! Indian medical facilities accessible via open border.", urgent: true, noVisa: true },
    { name: "Afghanistan", flag: "🇦🇫", processing: "7-10 days", embassy: "Kabul", notes: "Special provisions for medical emergencies. Hospital letter essential.", urgent: true, eVisa: false },
    { name: "Iraq", flag: "🇮🇶", processing: "7-10 days", embassy: "Baghdad, Erbil", notes: "Medical visa facilitation available. Growing patient base from Iraq.", eVisa: true },
    { name: "Yemen", flag: "🇾🇪", processing: "7-10 days", embassy: "Via UAE/Oman", notes: "Apply through Indian missions in neighboring countries.", eVisa: false },
    { name: "Somalia", flag: "🇸🇴", processing: "7-10 days", embassy: "Via Kenya", notes: "Apply through High Commission in Nairobi. Medical emergency provisions available.", eVisa: false },
    { name: "Sudan", flag: "🇸🇩", processing: "5-7 days", embassy: "Khartoum", notes: "Indian Embassy processes medical visas. Documentation support available.", eVisa: true },
    { name: "USA", flag: "🇺🇸", processing: "3-5 days", embassy: "Multiple cities", notes: "E-visa available. Many US-trained doctors in India. No interview required for medical visa.", urgent: true, eVisa: true },
    { name: "UK", flag: "🇬🇧", processing: "3-5 days", embassy: "London, Birmingham", notes: "E-visa available online. Quick processing for British citizens.", urgent: true, eVisa: true },
    { name: "Canada", flag: "🇨🇦", processing: "3-5 days", embassy: "Ottawa, Toronto, Vancouver", notes: "E-visa processing. Strong medical tourism to India.", urgent: true, eVisa: true },
    { name: "Australia", flag: "🇦🇺", processing: "3-5 days", embassy: "Canberra, Sydney", notes: "E-visa available. Direct flights to major Indian cities.", urgent: true, eVisa: true },
  ]
};

const faqs = [
  { q: "How long does it take to get a medical visa for India?", a: "Standard processing takes 5-10 working days. Urgent/emergency cases can be expedited to 2-3 days with proper documentation from the hospital." },
  { q: "Can my family accompany me on a medical visa?", a: "Yes, up to 2 family members can obtain Medical Attendant Visas (MX-Visa) to accompany the patient. They can stay as long as the patient's visa is valid." },
  { q: "What if I need to extend my stay for treatment?", a: "Medical visas can be extended based on medical necessity. The hospital provides documentation for extension, which is processed by the Foreigners Regional Registration Office (FRRO)." },
  { q: "Do I need travel insurance for medical visa?", a: "While not mandatory, we strongly recommend comprehensive travel insurance that covers medical emergencies, trip cancellation, and repatriation." },
];

export default function MedicalVisaPage() {
  const [selectedCountry, setSelectedCountry] = useState(null);

  return (
    <div data-testid="medical-visa-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-orange-50 to-white py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <Badge className="bg-orange-600/10 text-orange-600 border-orange-600/20 mb-4 font-body text-xs uppercase tracking-wider">
              Visa Assistance
            </Badge>
            <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-slate-900 mb-6">
              Medical Visa for <span className="text-orange-600">India</span>
            </h1>
            <p className="text-lg text-slate-600 mb-8">
              Complete guide and assistance for obtaining your Indian medical visa. We help patients from 50+ countries navigate the visa process smoothly.
            </p>
            <ConsultationModal
              trigger={
                <Button className="bg-orange-600 hover:bg-orange-700 text-white rounded-full px-8 py-6 font-semibold text-base">
                  Get Visa Assistance <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              }
            />
          </div>
        </div>
      </section>

      {/* Overview Cards */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { icon: Clock, title: "5-10 Days", desc: "Average processing time" },
              { icon: FileText, title: "1 Year Validity", desc: "Multiple entry allowed" },
              { icon: Shield, title: "60 Days/Visit", desc: "Per entry stay duration" },
              { icon: Globe, title: "50+ Countries", desc: "We assist patients from" },
            ].map((item, i) => (
              <Card key={i} className="bg-white rounded-2xl border border-slate-100 p-6 text-center">
                <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center mx-auto mb-3">
                  <item.icon className="h-6 w-6 text-orange-600" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-1">{item.title}</h3>
                <p className="text-sm text-slate-500">{item.desc}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Main Content Tabs */}
      <section className="py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Tabs defaultValue="overview" className="space-y-8">
            <TabsList className="flex flex-wrap justify-center gap-2 bg-transparent">
              <TabsTrigger value="overview" className="data-[state=active]:bg-orange-600 data-[state=active]:text-white rounded-full px-6 py-2">Overview</TabsTrigger>
              <TabsTrigger value="requirements" className="data-[state=active]:bg-orange-600 data-[state=active]:text-white rounded-full px-6 py-2">Requirements</TabsTrigger>
              <TabsTrigger value="process" className="data-[state=active]:bg-orange-600 data-[state=active]:text-white rounded-full px-6 py-2">Process</TabsTrigger>
              <TabsTrigger value="countries" className="data-[state=active]:bg-orange-600 data-[state=active]:text-white rounded-full px-6 py-2">By Country</TabsTrigger>
            </TabsList>

            <TabsContent value="overview">
              <Card className="bg-white rounded-2xl border border-slate-100 p-8">
                <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">{visaInfo.overview.title}</h2>
                <p className="text-slate-600 mb-6 leading-relaxed">{visaInfo.overview.description}</p>
                <h3 className="font-semibold text-slate-900 mb-3">Key Features:</h3>
                <ul className="space-y-3">
                  {visaInfo.overview.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-green-500 shrink-0 mt-0.5" />
                      <span className="text-slate-600">{f}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            </TabsContent>

            <TabsContent value="requirements">
              <Card className="bg-white rounded-2xl border border-slate-100 p-8">
                <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-6">Documents Required</h2>
                <div className="grid md:grid-cols-2 gap-4">
                  {visaInfo.requirements.map((req, i) => (
                    <div key={i} className="flex items-start gap-3 p-4 bg-slate-50 rounded-xl">
                      <div className="w-6 h-6 rounded-full bg-orange-100 flex items-center justify-center shrink-0">
                        <span className="text-orange-600 text-sm font-semibold">{i + 1}</span>
                      </div>
                      <span className="text-slate-700">{req}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-6 p-4 bg-amber-50 rounded-xl border border-amber-200">
                  <div className="flex gap-3">
                    <AlertCircle className="h-5 w-5 text-amber-600 shrink-0" />
                    <p className="text-amber-800 text-sm">
                      <strong>Important:</strong> We provide hospital invitation letters and assist with documentation to ensure your visa application is complete and accurate.
                    </p>
                  </div>
                </div>
              </Card>
            </TabsContent>

            <TabsContent value="process">
              <Card className="bg-white rounded-2xl border border-slate-100 p-8">
                <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-6">Visa Application Process</h2>
                <div className="space-y-6">
                  {visaInfo.process.map((step, i) => (
                    <div key={i} className="flex gap-4">
                      <div className="w-12 h-12 rounded-full bg-orange-600 text-white flex items-center justify-center font-bold text-lg shrink-0">
                        {step.step}
                      </div>
                      <div className="pt-2">
                        <h3 className="font-semibold text-slate-900 mb-1">{step.title}</h3>
                        <p className="text-slate-600">{step.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </TabsContent>

            <TabsContent value="countries">
              <Card className="bg-white rounded-2xl border border-slate-100 p-8">
                <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-2">Country-Specific Information</h2>
                <p className="text-slate-500 mb-6">Click on a country to view detailed visa requirements and notes</p>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {visaInfo.countries.map((country, i) => (
                    <div 
                      key={i} 
                      className={`p-4 border rounded-xl transition-all cursor-pointer ${
                        selectedCountry === i 
                          ? "border-orange-400 bg-orange-50 shadow-md" 
                          : "border-slate-200 hover:border-orange-300 hover:shadow-sm"
                      }`}
                      onClick={() => setSelectedCountry(selectedCountry === i ? null : i)}
                    >
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-2xl">{country.flag}</span>
                        <div>
                          <h3 className="font-semibold text-slate-900">{country.name}</h3>
                          <div className="flex gap-1 mt-1">
                            {country.noVisa && (
                              <Badge className="bg-green-100 text-green-700 text-[10px]">No Visa</Badge>
                            )}
                            {country.eVisa && !country.noVisa && (
                              <Badge className="bg-blue-100 text-blue-700 text-[10px]">E-Visa</Badge>
                            )}
                            {country.urgent && (
                              <Badge className="bg-orange-100 text-orange-700 text-[10px]">Fast Track</Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      <p className="text-sm text-slate-500 mb-1">
                        <strong>Processing:</strong> {country.processing}
                      </p>
                      <p className="text-sm text-slate-500 mb-1">
                        <strong>Embassy:</strong> {country.embassy}
                      </p>
                      {selectedCountry === i && (
                        <div className="mt-3 pt-3 border-t border-slate-200">
                          <p className="text-sm text-slate-600">{country.notes}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <div className="mt-6 p-4 bg-blue-50 rounded-xl border border-blue-200">
                  <p className="text-sm text-blue-800">
                    <strong>Don't see your country?</strong> We assist patients from 50+ countries. Contact us for visa guidance specific to your nationality.
                  </p>
                </div>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-12 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-8 text-center">
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <Card key={i} className="bg-white rounded-xl border border-slate-100 p-6">
                <div className="flex gap-3">
                  <HelpCircle className="h-5 w-5 text-orange-600 shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-2">{faq.q}</h3>
                    <p className="text-slate-600 text-sm leading-relaxed">{faq.a}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-orange-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Plane className="h-12 w-12 text-white mx-auto mb-4" />
          <h2 className="font-heading text-3xl font-semibold text-white mb-4">
            Need Help With Your Medical Visa?
          </h2>
          <p className="text-orange-100 mb-8 max-w-xl mx-auto">
            Our team assists thousands of patients every year with medical visa applications. Let us make your journey to India hassle-free.
          </p>
          <ConsultationModal
            trigger={
              <Button className="bg-white text-orange-600 hover:bg-orange-50 rounded-full px-10 py-6 font-semibold text-lg">
                Get Free Visa Guidance <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            }
          />
        </div>
      </section>
    </div>
  );
}
